import {
  Col,
  EditButton,
  Form,
  getDefaultSortOrder,
  Icons,
  Input,
  List,
  Row,
  Space,
  Table,
  TextField,
  useTable,
} from "@pankod/refine-antd";
import { CrudFilters, IResourceComponentsProps } from "@pankod/refine-core";
import type { CheckboxValueType } from "antd/es/checkbox/Group";
import type { ColumnsType } from "antd/es/table";
import { ButtonColumn } from "components/buttonColumn";
import { ButtonExport } from "components/buttonExport";
import { ButtonFilter } from "components/buttonFilter";
import React, { useState } from "react";
import { ILabel, ILabelResponse } from "types/label.interface";
import { formatDateTime, getOrderNumber, includesClassName } from "utils";
import routerProvider from "@pankod/refine-react-router-v6";
import useMediaScreen from "hooks/useMediaScreen";

export const LabelList: React.FC<IResourceComponentsProps> = () => {
  const { isMobile } = useMediaScreen();
  const { tableProps, sorter, searchFormProps }: any = useTable<ILabelResponse>(
    {
      resource: "labels",
      initialPageSize: 100,
      onSearch: (params) => {
        const filters: CrudFilters = [];
        const input = params;
        filters.push({
          field: "search",
          operator: "in",
          value: input,
        });
        return filters;
      },
    }
  );
  const { Link } = routerProvider;
  const current = tableProps?.pagination?.current;
  const pageSize = tableProps?.pagination?.pageSize;

  const defaultColumn: CheckboxValueType[] = [
    "name",
    "status",
    "minPrice",
    "maxPrice",
    "contributorPricing",
    "percentageOfTokensCanKeep",
    "defaultTrackPrice",
    "createdAt",
  ];

  const [hideCols, setHideCols] = useState<CheckboxValueType[]>(defaultColumn);

  const options = [
    { label: "Name", value: "name" },
    { label: "Biography", value: "biography" },
    { label: "Contributor Pricing", value: "contributorPricing" },
    { label: "Min Price", value: "minPrice" },
    { label: "MAx Price", value: "maxPrice" },
    { label: "Default Track Price", value: "defaultTrackPrice" },
    { label: "Active", value: "isActive" },
    { label: "Min Upload", value: "minUpload" },
    { label: "Max Upload", value: "maxUpload" },
    { label: "CreatedAt", value: "createdAt" },
    { label: "Number Downloads", value: "numberDownloads" },
    {
      label: "Default Split (%)",
      value: "percentageOfTokensCanKeep",
    },
  ];

  const columns: ColumnsType<ILabel> = [
    {
      title: "Index",
      dataIndex: "_id",
      key: "_id",
      render: (value, item, index) => (
        <TextField value={getOrderNumber(current, pageSize, index)} />
      ),
    },
    {
      title: "Order",
      dataIndex: "order",
      key: "order",
      render: (value) => <TextField value={value || 1} />,
      className: includesClassName(hideCols, "name"),
      defaultSortOrder: getDefaultSortOrder("name", sorter),
      sorter,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (_, record: any) => (
        <Link to={`/labels/edit/${record?._id}`}>{record?.name}</Link>
      ),
      className: includesClassName(hideCols, "name"),
      defaultSortOrder: getDefaultSortOrder("name", sorter),
      sorter,
    },
    {
      title: "Biography",
      dataIndex: "biography",
      key: "biography",
      render: (value) => <TextField value={value} />,
      className: includesClassName(hideCols, "biography"),
      defaultSortOrder: getDefaultSortOrder("biography", sorter),
      sorter,
    },
    {
      title: "Contributor Pricing",
      dataIndex: "contributorPricing",
      key: "contributorPricing",
      render: (value) => <TextField value={value ? "Yes" : "No"} />,
      className: includesClassName(hideCols, "contributorPricing"),
      defaultSortOrder: getDefaultSortOrder("contributorPricing", sorter),
      sorter,
    },
    {
      title: "Min Price",
      dataIndex: "minPrice",
      key: "minPrice",
      render: (_, record) => (
        <TextField
          value={record.contributorPricing ? record.minPrice : "N/A"}
        />
      ),
      className: includesClassName(hideCols, "minPrice"),
      defaultSortOrder: getDefaultSortOrder("minPrice", sorter),
      sorter,
    },
    {
      title: "Max Price",
      dataIndex: "maxPrice",
      key: "maxPrice",
      render: (_, record) => (
        <TextField
          value={record.contributorPricing ? record.maxPrice : "N/A"}
        />
      ),
      className: includesClassName(hideCols, "maxPrice"),
      defaultSortOrder: getDefaultSortOrder("maxPrice", sorter),
      sorter,
    },
    {
      title: "Default Track Price",
      dataIndex: "defaultTrackPrice",
      key: "defaultTrackPrice",
      render: (_, record) => (
        <TextField
          value={!record.contributorPricing ? record.defaultTrackPrice : "N/A"}
        />
      ),
      className: includesClassName(hideCols, "defaultTrackPrice"),
      defaultSortOrder: getDefaultSortOrder("defaultTrackPrice", sorter),
      sorter,
    },
    {
      title: "Active",
      dataIndex: "isActive",
      key: "isActive",
      render: (value) => <TextField value={value ? "Yes" : "No"} />,
      className: includesClassName(hideCols, "isActive"),
      defaultSortOrder: getDefaultSortOrder("isActive", sorter),
      sorter,
    },
    {
      title: "Min Upload",
      dataIndex: "minUpload",
      key: "minUpload",
      render: (value) => <TextField value={value} />,
      className: includesClassName(hideCols, "minUpload"),
      defaultSortOrder: getDefaultSortOrder("minUpload", sorter),
      sorter,
    },
    {
      title: "Max Upload",
      dataIndex: "maxUpload",
      key: "maxUpload",
      render: (value) => <TextField value={value} />,
      className: includesClassName(hideCols, "maxUpload"),
      defaultSortOrder: getDefaultSortOrder("maxUpload", sorter),
      sorter,
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (value) => <TextField value={formatDateTime(value)} />,
      className: includesClassName(hideCols, "createdAt"),
      defaultSortOrder: getDefaultSortOrder("createdAt", sorter),
      sorter,
    },
    {
      title: "Number Downloads",
      dataIndex: "numberDownloads",
      key: "numberDownloads",
      render: (value) => <TextField value={value} />,
      className: includesClassName(hideCols, "numberDownloads"),
      defaultSortOrder: getDefaultSortOrder("numberDownloads", sorter),
      sorter,
    },
    {
      title: "Default Split (%)",
      dataIndex: "percentageOfTokensCanKeep",
      key: "percentageOfTokensCanKeep",
      render: (value) => <TextField value={value} />,
      className: includesClassName(hideCols, "percentageOfTokensCanKeep"),
      defaultSortOrder: getDefaultSortOrder(
        "percentageOfTokensCanKeep",
        sorter
      ),
      sorter,
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      render: (_, record) => (
        <Space>
          <EditButton hideText size="small" recordItemId={record._id} />
        </Space>
      ),
    },
  ];

  return (
    <List>
      <Row style={{ display: "block" }}>
        <Col>
          <Form layout="vertical" {...searchFormProps}>
            <Form.Item
              label="Search"
              name="search"
              style={{ width: isMobile ? "100%" : "50%" }}
              initialValue=""
            >
              <Input
                placeholder="Name, Biography"
                prefix={<Icons.SearchOutlined />}
              />
            </Form.Item>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <Form.Item>
                <ButtonFilter />
              </Form.Item>
              <div>
                <ButtonColumn
                  options={options}
                  setHideCols={setHideCols}
                  defaultColumn={defaultColumn}
                />
                <ButtonExport path="labels" />
              </div>
            </div>
          </Form>
        </Col>
        <Col>
          <Table
            {...tableProps}
            pagination={{
              ...tableProps?.pagination,
              showSizeChanger: true,
            }}
            columns={columns}
            rowKey="id"
          />
        </Col>
      </Row>
    </List>
  );
};
